import { generateParams } from 'features/filters/filtersSlice';

import { get, post, put, putFormData } from 'utils/api/api';

import {
  Application,
  ApplicationFacet,
  ApplicationJobOptions,
  ApplicationWithOfferLetter,
  ApplicationsList,
  CreateApplication
} from 'types/applications';
import { User } from 'types/auth';
import { Clients } from 'types/clients';
import { BulkActionResultItem } from 'types/commons';
import { Filters } from 'types/filters';
import { InterviewConfirm, InterviewDetails } from 'types/interviewSlots';

import {
  APPLICATION,
  APPLICATIONS,
  APPLICATIONS_FACET,
  APPLICATIONS_IDS,
  APPLICATIONS_INVOICE_SENT_BULK,
  APPLICATIONS_INVOICE_SENT_DATE_BULK,
  APPLICATIONS_SEPARATION_DATE_BULK,
  APPLICATIONS_STAGE_BULK,
  APPLICATIONS_START_DATE_BULK,
  APPLICATION_JOBS_DICTIONARY,
  CLIENTS,
  COMPLETE,
  DECLINE,
  OFFER_LETTER,
  OFFER_LETTER_SEND,
  WITHDRAW
} from 'constants/endpoints';
import { ApplicationStage, ClientApplicationStages } from 'constants/enums';

class ApplicationsService {
  async getApplications(params: string): Promise<ApplicationsList> {
    const response = await get(`${APPLICATIONS}${params}`);
    return response;
  }

  async getAceApplications(filters: Filters): Promise<ApplicationsList> {
    const params = generateParams(filters);
    const response = await get(`${APPLICATIONS}${params}&ace=true`);
    return response;
  }

  async getApplicationById(id: string): Promise<Application> {
    const response = await get(`${APPLICATION}/${id}`);
    return response;
  }

  async getClientsBySearch(searchTerm: string): Promise<Clients> {
    const response = await get(`${CLIENTS}?searchTerm=${searchTerm}`);
    return response;
  }

  async getApplicationJobsDictionary(): Promise<ApplicationJobOptions> {
    const response = await get(`${APPLICATION_JOBS_DICTIONARY}`);
    return response;
  }

  async getApplicationsFacet(params: string): Promise<ApplicationFacet[]> {
    const response = await get(`${APPLICATIONS_FACET}${params}`);
    return response;
  }

  async changeApplicationStage(
    id: string,
    stage: ClientApplicationStages
  ): Promise<void> {
    const StageEndpoints: Partial<Record<ClientApplicationStages, string>> = {
      [ClientApplicationStages.DeclinedApplicationStageClient]: DECLINE,
      [ClientApplicationStages.CompletedApplicationStageClient]: COMPLETE
    };

    const endpoint = StageEndpoints[stage];

    if (endpoint) {
      await put(`${APPLICATION}/${id}${endpoint}`, {});
    }
  }

  async changeApplicationStageAndTags(
    id: string,
    value: ApplicationStage,
    tags: string[]
  ): Promise<void> {
    await put(`${APPLICATION}/${id}/stage/${value}/tags`, tags);
  }

  async assignRecruiters(id: string, recruitersIds: string[]): Promise<void> {
    const recruiters = recruitersIds.map((id) => ({ id }));
    await put(`${APPLICATION}/${id}/recruiter`, { recruiters });
  }

  async assignAceContact(id: string, user: User): Promise<void> {
    await put(`${APPLICATION}/${id}/ace_contact`, user);
  }

  async createNewApplication(data: CreateApplication): Promise<Application> {
    const result = await post(APPLICATION, data);
    return result;
  }

  async updateApplication(
    id: string,
    data: Partial<Application>
  ): Promise<Application> {
    const response = await put(`${APPLICATION}/${id}`, data);
    return response;
  }

  async getApplicationsIds(filters: Filters): Promise<string[]> {
    const params = generateParams(filters);
    const response = await get(`${APPLICATIONS_IDS}${params}`);
    return response || [];
  }

  async updateApplicationsStartDateBulk(
    ids: string[],
    startDate: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(APPLICATIONS_START_DATE_BULK, {
      ids,
      startDate
    });
    return response;
  }

  async updateApplicationsStageBulk(
    ids: string[],
    stage: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(APPLICATIONS_STAGE_BULK, {
      ids,
      stage
    });
    return response;
  }

  async updateApplicationsSeparationDateBulk(
    ids: string[],
    separationDate: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(APPLICATIONS_SEPARATION_DATE_BULK, {
      ids,
      separationDate
    });
    return response;
  }

  async updateApplicationsInvoiceSentDateBulk(
    ids: string[],
    invoiceSentDate: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(APPLICATIONS_INVOICE_SENT_DATE_BULK, {
      ids,
      invoiceSentDate
    });
    return response;
  }

  async updateApplicationsInvoiceSentBulk(
    ids: string[],
    invoiceSent: boolean
  ): Promise<BulkActionResultItem[]> {
    const response = await post(APPLICATIONS_INVOICE_SENT_BULK, {
      ids,
      invoiceSent
    });
    return response;
  }

  async getApplicationWithOfferLetter(
    applicationId: string
  ): Promise<ApplicationWithOfferLetter> {
    const response = await get(
      `${APPLICATION}/${applicationId}/${OFFER_LETTER}`
    );

    return response;
  }

  async generateAndSendApplicationOfferLetter(
    applicationId: string,
    formData: FormData
  ): Promise<void> {
    await putFormData(
      `${APPLICATION}/${applicationId}/${OFFER_LETTER_SEND}`,
      formData
    );
  }

  async withdrawApplicationOfferLetter({
    applicationId,
    offerLetterId
  }: {
    applicationId: string;
    offerLetterId: string;
  }): Promise<void> {
    await put(
      `${APPLICATION}/${applicationId}/${OFFER_LETTER}/${offerLetterId}/${WITHDRAW}`,
      {}
    );
  }

  async sendInterviewRequest(applicationId: string): Promise<void> {
    await post(`${APPLICATION}/${applicationId}/interview_request_send`, {});
  }

  async getApplicationInterviewRequest(
    applicationId: string
  ): Promise<InterviewDetails[]> {
    const response = await get(
      `${APPLICATION}/${applicationId}/interview_request`
    );

    return response;
  }

  async confirmInterview(
    applicationId: string,
    interviewRequestId: string,
    payload: InterviewConfirm
  ): Promise<void> {
    await put(
      `${APPLICATION}/${applicationId}/interview_request/${interviewRequestId}/accept`,
      payload
    );
  }

  async cancelInterview(
    applicationId: string,
    interviewRequestId: string
  ): Promise<void> {
    await put(
      `${APPLICATION}/${applicationId}/interview_request/${interviewRequestId}/withdraw`,
      {}
    );
  }
}

export default new ApplicationsService();
